import React, { useEffect, useRef, useState } from 'react';

import { ValueBox, ValueStepper } from '@candu-ai/craft/ui';
import { getCssResetStyleMessage, StyleHierarchy, useCss } from 'craft/hooks';

interface Props {
  nodeIds: string[];
}

export const TourStepZindex = ({ nodeIds }: Props) => {
  const { css: tourCss, setCss: setTourCss, stylesHierarchy } = useCss(nodeIds);

  const { fallbackValue, fallbackStyle } = stylesHierarchy?.['--z-index'] ?? {
    fallbackValue: undefined,
    fallbackStyle: undefined,
  };
  const inputRef = useRef<HTMLInputElement>(null);

  const value = tourCss['--z-index'];
  const defaultValue = fallbackValue ? parseFloat(fallbackValue as string) : 4000;
  const displayValue = value || defaultValue;

  const [inputValue, setInputValue] = useState<number | undefined>(displayValue);
  const [inEditMode, setInEditMode] = useState(false);

  const onSetValue = (newValue?: number) => {
    const valueChanged = newValue !== inputValue;
    setInEditMode(true);

    if (valueChanged) {
      setInputValue(newValue);

      // only trigger change when there is no value or the value is within the accepted range
      if (typeof newValue === 'undefined') {
        setTourCss({ '--z-index': undefined });
      } else if (newValue >= -500 && newValue <= 2147483647) {
        setTourCss({ '--z-index': newValue });
      }
    }
  };

  useEffect(() => {
    // when value or type changes due to external factors (caller auto-adjusted value/type, viewport changed, node changed, etc.)
    // if we're not in edit mode, reflect value/type change in input
    if (!inEditMode) {
      if (displayValue !== inputValue) {
        setInputValue(displayValue);
      }
    }
  }, [displayValue, inEditMode]);

  return (
    <ValueBox
      label="Z-index"
      tooltip="Z-index controls which elements appear in front of others on a webpage."
    >
      <ValueStepper
        ref={inputRef}
        step={1}
        name="tour-step-z-index"
        placeholder={!inEditMode ? 'unset' : undefined}
        editable
        format="inline"
        maxValue={2147483647}
        minValue={-500}
        onChange={onSetValue}
        onBlur={() => {
          setInEditMode(false);
        }}
        value={inputValue}
        onResetTooltip={getCssResetStyleMessage(fallbackStyle || StyleHierarchy.Styleguide)}
        onReset={
          typeof inputValue !== 'undefined' && inputValue !== defaultValue
            ? () => {
                setTourCss({ '--z-index': undefined });
                setInputValue(defaultValue);
              }
            : undefined
        }
      />
    </ValueBox>
  );
};
